import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  BULK_PDF_EXPORT_LIMIT,
  FormElement,
  FormElementKind,
  FormType,
  GetScheduleFormsRequestDTO,
  GetScheduleFormsResponseDTO,
  MODULE,
  PdfScope,
  ScheduleFormOrderByFieldEnumSchema,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import LinkViewIcon from '@/components/icons/link-view';
import PencilIcon from '@/components/icons/pencil';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useConditionalLogicEnabled } from '@/hooks/use-conditional-logic-enabled';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { buildFilterFields } from '@/queries/column-filter';
import { getFormByIdQueryOptions } from '@/queries/forms';
import { getScheduleFormsInfiniteQueryOptions, scheduleFormKeys } from '@/queries/schedule';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';
import { useEditFormNavigate } from '@/utils/routes';
import { useDropTableColumn } from '@/utils/table-meta';

import {
  NoScheduleFormSubmissionAvailableDisplay,
  NoScheduleFormSubmissionFoundFromFiltersDisplay,
  NoScheduleFormSubmissionFoundFromSearchDisplay,
  ScheduleFormFormSubmissionsTable,
} from './table';
import { FILTER_FIELD_MAP, getElementIdFromColumnId, groupOptions, isFormFieldColumn, columns as scheduleFormColumns } from './table-meta';
import { TopbarActions } from './topbar-actions';

const ScheduleFormFormSubmissionsPage = () => {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const handleEditScheduleFormForm = useEditFormNavigate();
  const { canManageScheduleForms, canEditScheduleRecord, canExportCsv } = usePermissions();

  const { id: formId } = useParams();

  const org = useUserStore((state) => state.org);

  const { data: formData } = useQuery(getFormByIdQueryOptions({ formId }));

  // Extract form elements from the form
  const formElements = useMemo<FormElement[]>(() => {
    return (formData?.form?.elements as FormElement[]) ?? [];
  }, [formData]);
  const conditionalLogicEnabled = useConditionalLogicEnabled(FormType.ScheduleForm);

  // Fetch units for unit reading columns
  const { data: unitsData } = useQuery(getUnitsQueryOptions());

  // Create dynamic columns with form elements and units
  const dynamicColumns = useMemo(() => scheduleFormColumns(formElements, unitsData), [formElements, unitsData]);

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.ScheduleFormFormSubmissions, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: dynamicColumns,
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  useDropTableColumn({
    module: MODULE.ScheduleFormFormSubmissions,
    columnIds: ['contact'],
  });

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  // Build formFields filter array for the API from form field column filters
  const formFieldFilters = useMemo(() => {
    if (!filters || !formElements.length) return undefined;

    const formFieldFilterList: { elementId: string; kind: FormElementKind; value: string | string[] | { from: string; to: string } }[] = [];

    for (const f of filters) {
      if (!isFormFieldColumn(f.id)) continue;

      const elementId = getElementIdFromColumnId(f.id);
      const element = formElements.find((e) => e.id === elementId);
      if (!elementId || !element || f.value === undefined || f.value === null || (Array.isArray(f.value) && f.value.length === 0)) continue;

      formFieldFilterList.push({
        elementId,
        kind: element.kind,
        value: f.value as string | string[] | { from: string; to: string },
      });
    }

    return formFieldFilterList.length > 0 ? formFieldFilterList : undefined;
  }, [filters, formElements]);

  // Build orderBy object, handling form field sorting specially
  const orderBy = useMemo(() => {
    if (!sorting.key) return undefined;

    if (isFormFieldColumn(sorting.key)) {
      const elementId = getElementIdFromColumnId(sorting.key);
      const element = formElements.find((e) => e.id === elementId);
      if (!elementId || !element) return undefined;

      return {
        field: 'formField' as const,
        direction: sorting.direction as 'asc' | 'desc',
        formField: { elementId, kind: element.kind },
      };
    }

    return {
      field: sorting.key as z.infer<typeof ScheduleFormOrderByFieldEnumSchema>,
      direction: sorting.direction as 'asc' | 'desc',
    };
  }, [sorting.key, sorting.direction, formElements]);

  const {
    data: scheduleFormsData,
    fetchNextPage: fetchNextScheduleFormsPage,
    hasNextPage: hasNextScheduleFormsPage,
    isFetching: isFetchingScheduleForms,
    isFetchingNextPage: isFetchingNextScheduleFormsPage,
    error: scheduleFormsError,
  } = useInfiniteQuery(
    getScheduleFormsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (columnId, raw) => (columnId === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
          extraFields: { formId: [formId as string], formFields: formFieldFilters },
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetScheduleFormsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedScheduleForms = useMemo(() => {
    return (incomingData: typeof scheduleFormsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteScheduleForms, isPending: isDeletingScheduleForms } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteScheduleForms({ scheduleFormIds: ids }),
    onSuccess: () => {
      toast.success('Schedule forms deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteScheduleForms = () => {
    if (scheduleFormsData?.pages) {
      const toDelete = getSelectedScheduleForms(scheduleFormsData);
      deleteScheduleForms(toDelete.map((scheduleForm) => scheduleForm.id));
    }
  };

  const selectedScheduleForms = useMemo(() => getSelectedScheduleForms(scheduleFormsData), [scheduleFormsData, getSelectedScheduleForms]);
  const canBulkActOnSelectedScheduleForms =
    selectedScheduleForms.length > 0 && selectedScheduleForms.every((scheduleForm) => canEditScheduleRecord(scheduleForm.submittedById));

  function buildScheduleFormsExportRequest(): RegisterExportQuery<'schedule-forms'> {
    const selected = getSelectedScheduleForms(scheduleFormsData);
    return {
      queryType: 'schedule-forms',
      filters: { fields: { id: selected.map((scheduleForm) => scheduleForm.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetScheduleFormsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!scheduleFormsData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildScheduleFormsExportRequest(), filename: buildCsvExportFilename('schedule-forms') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!scheduleFormsData?.pages) return;
    const records = getSelectedScheduleForms(scheduleFormsData);
    if ((scope === 'records' || scope === 'register-and-records') && records.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(buildPdfExportJob({ scope, recordType: 'schedule-form', records, register: buildScheduleFormsExportRequest() }));
  }

  useEffect(() => {
    if (scheduleFormsError) {
      toast.error(scheduleFormsError.message);
    }
  }, [toast, scheduleFormsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextScheduleFormsPage,
    hasNextPage: hasNextScheduleFormsPage,
    isFetching: isFetchingScheduleForms,
    isError: !!scheduleFormsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => scheduleFormsData?.pages.flatMap((page) => page.data) ?? [], [scheduleFormsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedScheduleForms.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <div className="text-sm">{formData?.form?.name}</div>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.ScheduleFormFormSubmissions, [])}
                    />
                  )}
                </div>

                <div className="flex h-full items-center">
                  <div className="flex items-center">
                    {formData?.form?.id && canManageScheduleForms && (
                      <Button
                        variant="ghost"
                        icon={<PencilIcon />}
                        className="hover:bg-grey-3 px-2 font-medium"
                        onClick={() => handleEditScheduleFormForm(formData?.form.id)}>
                        Edit Form
                      </Button>
                    )}

                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<GetScheduleFormsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={dynamicColumns}
                      formElements={conditionalLogicEnabled ? formElements : undefined}
                      defaultSectionLabel="Schedule Form"
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: string; active: boolean }> },
                        })
                      }
                    />
                    <TablePageHeaderActionButton
                      onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`)}
                      icon={<ShareIcon />}
                      label="Share Link"
                    />
                    <TablePageHeaderActionButton
                      onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`, '_blank')}
                      icon={<LinkViewIcon />}
                      label="Open Mechanic App"
                    />
                  </div>
                </div>
              </>
            )}

            {selectedScheduleForms.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedScheduleForms.length} totalCount={scheduleFormsData?.pages[0]?.totalCount ?? 0} />
                <>
                  {canExportCsv && (
                    <Button variant="ghost" icon={<DownloadCsvIcon />} className="hover:bg-grey-3 px-2 font-medium" onClick={handleCsvExport}>
                      Download CSV
                    </Button>
                  )}
                  <DownloadPdfMenu recordNoun="schedule" onSelect={handlePdfScopeSelect} canExportRegister={canExportCsv} />
                  {canBulkActOnSelectedScheduleForms && (
                    <Button
                      variant="ghost"
                      icon={<DeleteIcon className="text-default-red" />}
                      className="hover:bg-grey-3 px-2 font-medium"
                      onClick={() => setOpenDeleteConfirmation(true)}>
                      Delete
                    </Button>
                  )}
                </>
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <ScheduleFormFormSubmissionsTable
          data={flattenedData}
          loading={(isFetchingScheduleForms && !isFetchingNextScheduleFormsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoScheduleFormSubmissionFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoScheduleFormSubmissionFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.ScheduleFormFormSubmissions, [])} />
            ) : (
              <NoScheduleFormSubmissionAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.ScheduleFormFormSubmissions, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
          columns={dynamicColumns}
        />
        {hasNextScheduleFormsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextScheduleFormsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteScheduleForms}
        loading={isDeletingScheduleForms}
        title="Delete schedule forms?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these schedule forms?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
};

export default ScheduleFormFormSubmissionsPage;
